import React, { useEffect, useRef, useState } from "react";
import {
  AutoAwesomeRounded,
  CheckRounded,
  ChevronRightRounded,
  DescriptionOutlined,
  ForumOutlined,
  FavoriteBorderRounded,
  FlagOutlined,
  HistoryRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  LightbulbOutlined,
  LockOutlined,
  RemoveRedEyeOutlined,
  RestartAltRounded,
} from "@mui/icons-material";
import {
  MAYA_NOTE,
  MEETING_INSIGHTS,
  MEETING_QUESTIONS,
  ORGANIZED_DETAILS,
  PROFILE_SECTIONS,
  TIMELINE_ENTRIES,
} from "./demoData";

const STEP_LABELS = ["Capture", "Organize", "Timeline", "Profile", "Prepare"];
const PROFILE_ICONS = {
  chat: ForumOutlined,
  heart: FavoriteBorderRounded,
  eye: RemoveRedEyeOutlined,
  goal: FlagOutlined,
};

const DemoButton = ({ children, secondary = false, ...props }) => (
  <button className={`interactive-demo-button${secondary ? " secondary" : ""}`} type="button" {...props}>
    {children}
  </button>
);

const SourceDisclosure = ({ source, open, onToggle }) => (
  <div className="demo-source">
    <button type="button" onClick={onToggle} aria-expanded={open}>
      <DescriptionOutlined fontSize="small" /> Original source
      {open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
    </button>
    {open && <p>{source}</p>}
  </div>
);

const DemoProgress = ({ step }) => (
  <div className="demo-progress" aria-label={`Demo step ${Math.min(step + 1, 5)} of 5`}>
    {STEP_LABELS.map((label, index) => (
      <div className={`demo-progress-item${index <= step ? " active" : ""}`} key={label}>
        <span>{index < step ? <CheckRounded /> : index + 1}</span>
        <small>{label}</small>
      </div>
    ))}
  </div>
);

const CaptureStep = ({ note, setNote, onAdd }) => (
  <div className="demo-step capture-step">
    <div className="demo-step-copy">
      <span className="demo-kicker">1 · Capture naturally</span>
      <h2>Start with what you already have.</h2>
      <p>Paste a school message, speak a quick thought, or add a note. No forms to figure out first.</p>
    </div>
    <div className="capture-card">
      <div className="capture-person"><span>M</span><div><strong>Maya</strong><small>Care note · Today</small></div></div>
      <label htmlFor="maya-note">What happened?</label>
      <textarea id="maya-note" value={note} onChange={(event) => setNote(event.target.value)} />
      <div className="capture-actions">
        <span><LockOutlined /> Private to Maya’s care circle</span>
        <DemoButton onClick={onAdd} disabled={!note.trim()}>Add to Lifelog <ChevronRightRounded /></DemoButton>
      </div>
    </div>
  </div>
);

const OrganizeStep = ({ processing, onContinue }) => (
  <div className="demo-step organize-step">
    <div className={`organize-orb${processing ? " processing" : " done"}`}><AutoAwesomeRounded /></div>
    {processing ? (
      <><span className="demo-kicker">Lifelog is organizing this moment</span><h2>Finding the useful care context…</h2><div className="organize-lines"><i /><i /><i /></div></>
    ) : (
      <>
        <span className="demo-kicker">2 · AI organizes it</span>
        <h2>Lifelog found the parts worth remembering.</h2>
        <div className="found-tags">{["School event", "Transition difficulty", "Communication", "Strategy that helped"].map((item) => <span key={item}><CheckRounded />{item}</span>)}</div>
        <div className="organized-grid">{ORGANIZED_DETAILS.map((item) => <article key={item.label}><small>{item.label}</small><p>{item.text}</p></article>)}</div>
        <div className="trust-note"><DescriptionOutlined /><div><strong>Original note preserved</strong><span>Lifelog helps organize. You stay in control of what belongs in Maya’s story.</span></div></div>
        <DemoButton onClick={onContinue}>See Maya’s timeline <ChevronRightRounded /></DemoButton>
      </>
    )}
  </div>
);

const TimelineStep = ({ onContinue }) => {
  const [openId, setOpenId] = useState("aug-18");
  return (
    <div className="demo-step timeline-step">
      <div className="demo-step-copy split"><div><span className="demo-kicker">3 · A care history takes shape</span><h2>Maya’s timeline</h2></div><p>The moments stay connected to when they happened — and to the words they came from.</p></div>
      <div className="timeline-list">
        {TIMELINE_ENTRIES.map((entry) => {
          const open = openId === entry.id;
          return <article className={`timeline-entry${open ? " open" : ""}`} key={entry.id}>
            <button type="button" onClick={() => setOpenId(open ? "" : entry.id)} aria-expanded={open}>
              <time>{entry.date}</time><div><h3>{entry.title}</h3><p>{entry.summary}</p></div>{open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
            </button>
            {open && <div className="timeline-details"><div className="detail-tags">{entry.details.map((detail) => <span key={detail}>{detail}</span>)}</div><SourceDisclosure source={entry.source} open onToggle={() => setOpenId("")} /></div>}
          </article>;
        })}
      </div>
      <DemoButton onClick={onContinue}>See what Lifelog remembers <ChevronRightRounded /></DemoButton>
    </div>
  );
};

const ProfileStep = ({ onContinue }) => (
  <div className="demo-step profile-step">
    <div className="profile-heading"><div className="maya-avatar">M</div><div><span className="demo-kicker">4 · What Lifelog remembers</span><h2>What Lifelog knows about Maya</h2><p>Built from the moments you already captured.</p></div></div>
    <div className="profile-grid">{PROFILE_SECTIONS.map((section) => { const Icon = PROFILE_ICONS[section.icon]; return <article key={section.title}><div className="profile-icon"><Icon /></div><h3>{section.title}</h3><ul>{section.items.map((item) => <li key={item}><CheckRounded />{item}</li>)}</ul><small><HistoryRounded /> Learned across Maya’s care history</small></article>; })}</div>
    <div className="profile-foot"><p>No giant profile to maintain. Lifelog builds context as care happens.</p><DemoButton onClick={onContinue}>Put the history to work <ChevronRightRounded /></DemoButton></div>
  </div>
);

const MeetingPrepStep = ({ onContinue }) => {
  const [openInsight, setOpenInsight] = useState(0);
  return (
    <div className="demo-step meeting-step">
      <div className="meeting-header"><span className="demo-kicker">5 · Prepare for a conversation</span><h2>What may be useful to discuss</h2><p>A calm starting point for an IEP, therapy, school, doctor, or care-team meeting.</p><span className="meeting-badge"><AutoAwesomeRounded /> Prepared from Maya’s history</span></div>
      <div className="meeting-layout"><div className="insight-list">{MEETING_INSIGHTS.map((insight, index) => {
        const open = openInsight === index;
        const entries = TIMELINE_ENTRIES.filter((entry) => insight.entryIds.includes(entry.id));
        return <article className="insight-card" key={insight.title}><LightbulbOutlined /><div><h3>{insight.title}</h3><p>{insight.text}</p><button type="button" onClick={() => setOpenInsight(open ? -1 : index)} aria-expanded={open}>{insight.evidenceLabel}{open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}</button>{open && <div className="evidence-list">{entries.map((entry) => <div key={entry.id}><time>{entry.date}</time><span>{entry.summary}</span></div>)}</div>}</div></article>;
      })}</div><aside><h3>Questions to consider</h3><ul>{MEETING_QUESTIONS.map((question) => <li key={question}>{question}</li>)}</ul><div className="evidence-promise"><LockOutlined /><p><strong>Grounded in the record</strong><span>Every suggestion connects back to Maya’s original moments. Lifelog doesn’t invent conclusions.</span></p></div></aside></div>
      <DemoButton onClick={onContinue}>Finish the story <ChevronRightRounded /></DemoButton>
    </div>
  );
};

const FinalStep = ({ onRestart }) => (
  <div className="demo-step final-step">
    <div className="final-mark"><FavoriteBorderRounded /></div>
    <p>You capture the moments.</p><h2>Lifelog helps you remember the story.</h2><div className="final-rule" /><h3>Care that remembers.</h3>
    <div><DemoButton secondary onClick={onRestart}><RestartAltRounded /> Start again</DemoButton><a className="interactive-demo-button" href="/login">Explore Lifelog <ChevronRightRounded /></a></div>
  </div>
);

const GuidedDemo = React.forwardRef(({ id = "demo" }, ref) => {
  const [step, setStep] = useState(0);
  const [note, setNote] = useState(MAYA_NOTE);
  const [processing, setProcessing] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);
  const advanceFromCapture = () => {
    setStep(1); setProcessing(true);
    timerRef.current = window.setTimeout(() => setProcessing(false), 1250);
  };
  const restart = () => { setNote(MAYA_NOTE); setProcessing(false); setStep(0); ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <section className="guided-demo" id={id} ref={ref} aria-label="Interactive Lifelog demo">
      <div className="demo-shell">
        {step < 5 && <DemoProgress step={step} />}
        <div className="demo-content" key={step}>
          {step === 0 && <CaptureStep note={note} setNote={setNote} onAdd={advanceFromCapture} />}
          {step === 1 && <OrganizeStep processing={processing} onContinue={() => setStep(2)} />}
          {step === 2 && <TimelineStep onContinue={() => setStep(3)} />}
          {step === 3 && <ProfileStep onContinue={() => setStep(4)} />}
          {step === 4 && <MeetingPrepStep onContinue={() => setStep(5)} />}
          {step === 5 && <FinalStep onRestart={restart} />}
        </div>
      </div>
    </section>
  );
});

GuidedDemo.displayName = "GuidedDemo";
export default GuidedDemo;
